import React, { type ReactNode } from 'react'
import { usePage } from '@inertiajs/react'

import { useAlertsStore } from '@stores/alertsStore'
import type { AlertsPathType, AlertType } from '@stores/alertsStore'
import Alert from '@atoms/Alert'

type AppAlertsProps = {
  alertsPath: AlertsPathType
  className?: string
}

type AppAlertsWrapperProps = {
  children: ReactNode | ReactNode[]
  className?: string
}

type ZustandAlertsProps = {
  alerts: AlertType[]
  removeAlert: (indexToRemove: number) => void
}

type FlashKeyTypes = 'notice' | 'alert' | 'success' | 'warning' | 'danger'
type FlashType = Partial<Record<FlashKeyTypes, string | null | undefined>>

const ZustandAlerts = ({ alerts, removeAlert }: ZustandAlertsProps) => {
  return (
    <>
      {alerts?.map(({ type, details, icon }, index) => (
        <Alert
          key={`${details.title}-${index}`}
          type={type}
          icon={icon}
          title={details.title}
          onClose={() => {
            removeAlert(index)
          }}
        >
          <p className="tw-text-md">{details.description}</p>
        </Alert>
      ))}
    </>
  )
}

const FlashAlerts = () => {
  const initialFlash = usePage().props.flash as FlashType | null | undefined
  const [flash, setFlash] = React.useState(initialFlash)
  const flashKeys = Object.keys(flash ?? {}) as FlashKeyTypes[]

  const getFlashAlertType = (flashKey: FlashKeyTypes) => {
    return {
      notice: 'info' as const,
      alert: 'error' as const,
      success: 'success' as const,
      warning: 'warning' as const,
      danger: 'error' as const,
    }[flashKey]
  }

  if (!flash || flashKeys.length === 0) return null

  return (
    <>
      {flashKeys.map((key) => {
        if (!flash[key]) return null
        return (
          <Alert
            key={key}
            type={getFlashAlertType(key)}
            title={flash[key]}
            onClose={() => {
              setFlash({ ...flash, [key]: null })
            }}
          />
        )
      })}
    </>
  )
}

const AppAlertsWrapper = ({ children, className }: AppAlertsWrapperProps) => {
  const filteredChildren = React.Children.toArray(children).filter(Boolean)
  if (filteredChildren.length === 0) return null

  return (
    <div data-testid="app-alerts" className={`tw-space-y-2 empty:tw-hidden ${className ?? ''}`}>
      {filteredChildren}
    </div>
  )
}

export const AppAlerts = ({ alertsPath, className }: AppAlertsProps) => {
  const { alerts: allAlerts, clearAlert } = useAlertsStore(({ alerts, clearAlert }) => ({
    alerts,
    clearAlert,
  }))
  const alerts = allAlerts[alertsPath] ?? []
  const removeAlert = (indexToRemove: number) => {
    clearAlert(alertsPath, indexToRemove)
  }

  return (
    <AppAlertsWrapper className={className}>
      {alertsPath === 'flash' && <FlashAlerts />}
      {alerts.length > 0 && <ZustandAlerts alerts={alerts} removeAlert={removeAlert} />}
    </AppAlertsWrapper>
  )
}
